import {
    Component,
    forwardRef,
    Input,
    ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-progress-bar-input',
    template: `
        <div class="flex flex-col gap-4">
            <!-- Input Group -->
            <div class="flex items-center gap-3">
                <!-- Decrease Button -->
                <button
                    type="button"
                    (click)="decrease()"
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                    [disabled]="percent === 0"
                >
                    <i class="fa-light fa-minus"></i>
                </button>

                <!-- Value Display -->
                <div
                    class="relative flex h-12 min-w-[120px] flex-1 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50"
                >
                    <input
                        type="number"
                        [ngModel]="percent"
                        (ngModelChange)="updateValue($event)"
                        min="0"
                        max="100"
                        class="hide-arrows absolute inset-0 w-full text-center text-2xl font-bold text-slate-800"
                        style="border: none !important; background: transparent !important; box-shadow: none !important; outline: none !important; padding: 0 !important; height: 100% !important; border-radius: 0 !important;"
                    />
                    <span
                        class="pointer-events-none absolute text-sm font-medium text-slate-400 ltr:right-3 rtl:left-3"
                        >%</span
                    >
                </div>

                <!-- Increase Button -->
                <button
                    type="button"
                    (click)="increase()"
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                    [disabled]="percent === 100"
                >
                    <i class="fa-light fa-plus"></i>
                </button>
            </div>

            <!-- Progress Bar -->
            <div class="h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
                <div
                    class="h-full rounded-full transition-all duration-300"
                    [class]="getProgressColor()"
                    [style.width.%]="percent ?? 0"
                ></div>
            </div>

            <!-- Quick Select Buttons -->
            <div class="flex flex-wrap items-center gap-2">
                <span class="text-xs font-medium text-slate-400"
                    >{{ 'translate_quick_select' | translate }}:</span
                >
                @for (value of quickValues; track value) {
                    <button
                        type="button"
                        (click)="updateValue(value)"
                        class="rounded-lg px-3 py-1.5 text-xs font-medium transition-all"
                        [class]="
                            percent === value
                                ? 'bg-primary-100 text-primary-700'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        "
                    >
                        {{ value }}%
                    </button>
                }
            </div>
        </div>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ProgressBarInputComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ProgressBarInputComponent implements ControlValueAccessor {
    @Input() public percent: number | null = null;

    @Input() public percentStep: number = 10;

    public quickValues = [0, 25, 50, 75, 100];

    private onChange: (value: number) => void;

    public increase(): void {
        const value = Math.min((this.percent ?? 0) + this.percentStep, 100);
        this.updateValue(value);
    }

    public decrease(): void {
        const value = Math.max((this.percent ?? 0) - this.percentStep, 0);
        this.updateValue(value);
    }

    public getProgressColor(): string {
        const p = this.percent ?? 0;
        if (p >= 80) return 'bg-emerald-500';
        if (p >= 50) return 'bg-amber-500';
        if (p > 0) return 'bg-rose-500';
        return 'bg-slate-300';
    }

    public writeValue(value: any): void {
        this.percent = value == null ? null : +value;
    }

    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    public registerOnTouched(): void {
        return undefined;
    }

    public updateValue(value: number): void {
        // Clamp value between 0 and 100
        const clampedValue = Math.max(0, Math.min(100, value ?? 0));
        this.percent = clampedValue;
        if (this.onChange) {
            this.onChange(clampedValue);
        }
    }
}
